import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { claimShipment, getUnassignedWork } from '../../api/endpoints/ops';
import { operationalQueryPolicy, safePagePlaceholder } from '../../api/queryPolicies';
import { shipmentStatusColor } from '../../components/domain/statusColor';
import { DataTable } from '../../components/ui/DataTable';
import { Pagination } from '../../components/ui/Pagination';
import { RefreshButton } from '../../components/ui/RefreshButton';
import { shipmentStatusLabel } from '../shipments/shipmentStatusLabel';
import { StatusPill } from '../../components/ui/StatusPill';
import { toast } from '../../components/ui/Toast';
import { OWNERSHIP_INVALIDATE_KEYS } from '../ownership/opsUser';
import { ownershipErrorMessage } from '../ownership/ownershipErrors';
import { fmtAge, fmtDate } from './workFormat';
import { VehicleCell } from '../../components/domain/VehicleCell';
import styles from './WorkQueue.module.css';
import { RouteText } from '../../components/domain/RouteText';

const PAGE_SIZE = 25;

/** Open & unowned orders, oldest first, with a per-row Claim. */
export function UnassignedView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});

  const filterState = location.state as { filterIds?: number[]; filterLabel?: string } | null;
  const [filterIds, setFilterIds] = useState<number[] | null>(filterState?.filterIds ?? null);
  const filterLabel = filterState?.filterLabel ?? 'Needs attention';

  const queryKey = ['admin', 'work', 'unassigned', 'list', page] as const;
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => getUnassignedWork({ page, size: PAGE_SIZE }),
    ...operationalQueryPolicy,
    placeholderData: safePagePlaceholder(queryKey, 4),
  });

  const invalidate = () =>
    OWNERSHIP_INVALIDATE_KEYS.forEach((key) => void queryClient.invalidateQueries({ queryKey: key }));

  const claim = useMutation({
    mutationFn: (shipmentId: number) => claimShipment(shipmentId),
    onSuccess: (_data, shipmentId) => {
      setRowErrors(({ [shipmentId]: _cleared, ...rest }) => rest);
      toast.success(`Shipment #${shipmentId} claimed — it’s now in My Work.`);
      invalidate();
    },
    onError: (error, shipmentId) => {
      const message = ownershipErrorMessage(error);
      setRowErrors((prev) => ({ ...prev, [shipmentId]: message }));
      toast.error(message);
      // A lost race / closed order means the queue is stale.
      invalidate();
    },
  });

  const navState = { from: '/unassigned', fromLabel: 'Unassigned' };

  const displayedRows = filterIds
    ? (data?.items ?? []).filter((s) => filterIds.includes(s.id))
    : data?.items ?? [];

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.summary}>
          {data ? (
            <span>
              <strong>{data.totalItems}</strong>{' '}
              {t('work.unassignedView.withoutOwner', { count: data.totalItems })}
            </span>
          ) : (
            'Open orders without an owner'
          )}
        </div>
        <RefreshButton queryKeys={[['admin', 'work']]} />
      </div>

      {filterIds && (
        <div className={styles.filterBanner}>
          <span>
            {t('work.unassignedView.filteredBy')} <strong>{filterLabel}</strong>{' '}
            {t('work.unassignedView.filteredCount', { count: displayedRows.length })}
          </span>
          <button
            type="button"
            className={styles.clearFilterBtn}
            onClick={() => setFilterIds(null)}
          >
            {t('work.unassignedView.clearFilter')}
          </button>
        </div>
      )}

      <DataTable
        loading={isLoading}
        error={isError ? 'We couldn’t load unassigned orders' : undefined}
        onRetry={() => refetch()}
        keyFn={(s) => s.id}
        rows={displayedRows}
        onRowClick={(s) => navigate(`/shipments/${s.id}`, { state: navState })}
        empty={
          filterIds
            ? `No orders matching “${filterLabel}” on this page`
            : 'Every open order has an owner'
        }
        columns={[
          {
            key: 'id',
            header: 'ID',
            width: '72px',
            render: (s) => (
              <Link to={`/shipments/${s.id}`} state={navState} className={styles.detailLink}>
                #{s.id}
              </Link>
            ),
          },
          {
            key: 'route',
            header: 'Route',
            render: (s) => (
              <Link to={`/shipments/${s.id}`} state={navState} className={styles.route}>
                <RouteText route={s} />
                <span className={styles.sub}>{s.shipperName ?? `Shipper #${s.shipperId}`}</span>
              </Link>
            ),
          },
          {
            key: 'vehicle',
            header: 'Vehicle',
            width: '140px',
            render: (s) => <VehicleCell assignment={s.assignment} />,
          },
          { key: 'pickup', header: 'Pickup', width: '120px', render: (s) => fmtDate(s.pickupDate) },
          {
            key: 'status',
            header: 'Status',
            width: '150px',
            render: (s) => {
              const c = shipmentStatusColor(s.status);
              return <StatusPill bg={c.bg} text={c.text} label={shipmentStatusLabel(s.status)} title={s.status} />;
            },
          },
          {
            key: 'waiting',
            header: 'Waiting',
            width: '110px',
            render: (s) => (
              <span title={new Date(s.createdAt).toLocaleString('en-IN')}>{fmtAge(s.createdAt)}</span>
            ),
          },
          {
            key: 'claim',
            header: '',
            width: '120px',
            render: (s) => {
              const pending = claim.isPending && claim.variables === s.id;
              return (
                <>
                  <button
                    type="button"
                    className={styles.claimBtn}
                    disabled={pending}
                    aria-label={`Claim shipment ${s.id}`}
                    onClick={() => claim.mutate(s.id)}
                  >
                    {pending ? 'Claiming…' : 'Claim'}
                  </button>
                  {rowErrors[s.id] && (
                    <span className={styles.rowError} role="alert">
                      {rowErrors[s.id]}
                    </span>
                  )}
                </>
              );
            },
          },
        ]}
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          totalItems={data.totalItems}
          size={data.size}
          onPage={setPage}
        />
      )}
    </div>
  );
}
